/**
 * KPI Overview Tab - Main screen component.
 * Refactored: helpers → utils/kpiHelpers.ts, API → services/kpiOverviewApi.ts,
 * data hook → hooks/useKpiOverview.ts, card → components/KPICard.tsx,
 * section → components/KPISection.tsx, styles → styles/kpiOverviewStyles.ts.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	Platform,
	ScrollView,
	Text,
	TextInput,
	TouchableOpacity,
	View,
	useWindowDimensions,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useSelector } from 'react-redux';
import { useTheme } from '../../../../theme/useTheme';
import { RootState } from '../../../../store';
import { useRegionMarkets } from '../../../../hooks/useRegionMarkets';
import { usePeriodWeeks } from '../../hooks/usePeriodWeeks';
import { useDisabledPeriodWeekIds } from '../../hooks/useDisabledPeriodWeekIds';
import KPIFilterDropdown from '../../components/KPIFilterDropdown';
import KPIOverviewLoadingSkeleton from '../../components/KPIOverviewLoadingSkeleton';
import KPISection, { EmptyState } from '../../components/KPISection';
import KPICard from '../../components/KPICard';
import TimeFrameSelector, { TimeFrameOption } from '../../components/TimeFrameSelector';
import CustomViewsButton from '../../components/CustomViewsButton';
import DateFilterPicker from '../../components/DateFilterPicker';
import { getAvailableDates, getApiDateRange } from '../../utils/dateFilterHelpers';
import { getTimeFrameDateRange, TimeFrameKey } from '../../utils/timeFrameHelpers';
import {
	DateSelectionState,
	createDateRangeSelection,
	createSingleDateSelection,
} from '../../types/dateSelectionTypes';
import KPITrendModal from './KPITrendModal';
import { useKpiOverview } from '../../hooks/useKpiOverview';
import {
	KpiMetric,
	KpiCategory,
	LinkedReportInfo,
	KPI_CATEGORY_FILTER_ORDER,
	KPI_CATEGORY_COLORS,
	KPI_CATEGORY_COLORS_DARK,
	inferPlatformFromLink,
	normalizeFilterToken,
	resolveKpiCategory,
} from '../../utils/kpiHelpers';
import { createKpiOverviewStyles } from '../../styles/kpiOverviewStyles';
import { getKpiSortMode, setKpiSortMode } from '../../utils/kpiSortSession';

type FilterDropdownKey = 'region' | 'market' | 'location' | 'year' | 'period' | 'week' | 'locationType' | 'day';

interface LHDropdownItem {
	id: string | number;
	displayText: string;
	[key: string]: any;
}

export interface KPIOverviewTabProps {
	regionOptions: LHDropdownItem[];
	marketOptions: LHDropdownItem[];
	locationOptions: LHDropdownItem[];
	locationTypeOptions?: LHDropdownItem[];
	selectedLocationTypes?: LHDropdownItem[];
	onLocationTypeChange?: (value: LHDropdownItem | LHDropdownItem[] | null) => void;
	yearOptions: LHDropdownItem[];
	periodOptions: LHDropdownItem[];
	weekOptions: LHDropdownItem[];
	region: LHDropdownItem | null;
	market: LHDropdownItem | null;
	location: LHDropdownItem[];
	year: LHDropdownItem | null;
	period: LHDropdownItem | null;
	weekInYear: LHDropdownItem | null;
	onRegionChange: (value: LHDropdownItem | LHDropdownItem[] | null) => void;
	onMarketChange: (value: LHDropdownItem | LHDropdownItem[] | null) => void;
	onLocationChange: (value: LHDropdownItem | LHDropdownItem[] | null) => void;
	onYearChange: (value: LHDropdownItem | LHDropdownItem[] | null) => void;
	onPeriodChange: (value: LHDropdownItem | LHDropdownItem[] | null) => void;
	onWeekInYearChange: (value: LHDropdownItem | LHDropdownItem[] | null) => void;
	isMarketDisabled?: boolean;
	isLocationDisabled?: boolean;
	locationPlaceholder?: string;
	showDashboardContent?: boolean;
	onCustomizeKpisPress?: () => void;
	/**
	 * When provided, clicking a KPI card's Power BI / Tableau badge will navigate to the
	 * Reports tab with the KPI name pre-applied as a search filter, instead of opening
	 * the external report link.
	 */
	onNavigateToReports?: (kpiName: string) => void;
	/**
	 * Callback to report the current API date range (startDate/endDate) to the parent.
	 * Used so sibling tabs (e.g. Location Details) can receive the same date values.
	 */
	onDateRangeChange?: (startDate?: string, endDate?: string) => void;
	selectedTimeFrame?: TimeFrameOption | null;
	onSelectedTimeFrameChange?: (value: TimeFrameOption | null) => void;
	dateSelection?: DateSelectionState;
	onDateSelectionChange?: (value: DateSelectionState) => void;
	onMobileFilterOverlayVisibleChange?: (visible: boolean) => void;
	mobileFilterButtonRightAligned?: boolean;
	/**
	 * Called after filters are committed via Apply or Reset (i.e. once the parent's
	 * committed filter state has settled). Used by consumers (e.g. the One Pager V2 tab)
	 * that need to snapshot filters and only re-fetch on Apply/Reset, not on every
	 * intermediate draft change.
	 */
	onApplyFilters?: () => void;
	/**
	 * When true, externally triggers the mobile filter overlay to open.
	 * Controlled by parent (e.g. a shared Filters button above the tabs).
	 */
	mobileFilterOverlayOpen?: boolean;
	/** When true, hides the internal mobile filter trigger button (used when the
	 *  parent renders its own shared Filters button above the tabs). */
	hideMobileFilterButton?: boolean;
	/** Full multi-select region array from parent — used for cross-tab draft sync on mount. */
	selectedRegions?: LHDropdownItem[];
	/** Full multi-select market array from parent — used for cross-tab draft sync on mount. */
	selectedMarkets?: LHDropdownItem[];
	/** Full multi-select year array from parent — used for cross-tab draft sync on mount and hasChanges comparison. */
	selectedYears?: LHDropdownItem[];
	/** Full multi-select period array from parent — used for cross-tab draft sync on mount. */
	selectedPeriods?: LHDropdownItem[];
	/** Full multi-select week array from parent — used for cross-tab draft sync on mount. */
	selectedWeeks?: LHDropdownItem[];
}

const KPIOverviewScreen: React.FC<KPIOverviewTabProps> = ({
	regionOptions,
	marketOptions,
	locationOptions,
	locationTypeOptions = [],
	selectedLocationTypes = [],
	onLocationTypeChange,
	yearOptions,
	periodOptions,
	weekOptions,
	region,
	market,
	location,
	year,
	period,
	weekInYear,
	onRegionChange,
	onMarketChange,
	onLocationChange,
	onYearChange,
	onPeriodChange,
	onWeekInYearChange,
	isMarketDisabled = false,
	isLocationDisabled = false,
	locationPlaceholder = 'Select...',
	showDashboardContent = true,
	onCustomizeKpisPress,
	onNavigateToReports,
	onDateRangeChange,
	selectedTimeFrame: selectedTimeFrameProp,
	onSelectedTimeFrameChange,
	dateSelection: dateSelectionProp,
	onDateSelectionChange,
	onMobileFilterOverlayVisibleChange,
	mobileFilterButtonRightAligned = false,
	onApplyFilters,
	mobileFilterOverlayOpen = false,
	hideMobileFilterButton = false,
	selectedRegions: selectedRegionsProp,
	selectedMarkets: selectedMarketsProp,
	selectedYears: selectedYearsProp,
	selectedPeriods: selectedPeriodsProp,
	selectedWeeks: selectedWeeksProp,
}) => {
	const { colors, resolvedTheme } = useTheme();
	const isDark = resolvedTheme === 'dark';
	const styles = createKpiOverviewStyles(isDark, colors);

	const { width, height } = useWindowDimensions();
	const isMobileNativeView = Platform.OS !== 'web' && width < 768;
	const [showWins, setShowWins] = useState(false);
	const [selectedKpi, setSelectedKpi] = useState<KpiMetric | null>(null);
	const [modalVisible, setModalVisible] = useState(false);
	const [draftRegion, setDraftRegion] = useState<LHDropdownItem | null>(region);
	const [draftMarket, setDraftMarket] = useState<LHDropdownItem | null>(market);
	const [draftLocation, setDraftLocation] = useState<LHDropdownItem[]>(location);
	const [draftYear, setDraftYear] = useState<LHDropdownItem | null>(year);
	const [draftPeriod, setDraftPeriod] = useState<LHDropdownItem | null>(period);
	const [draftWeekInYear, setDraftWeekInYear] = useState<LHDropdownItem | null>(weekInYear);
	const [isMobileFilterOverlayVisible, setMobileFilterOverlayVisible] = useState(false);
		useEffect(() => {
			if (onMobileFilterOverlayVisibleChange) {
				onMobileFilterOverlayVisibleChange(isMobileFilterOverlayVisible);
			}
		}, [isMobileFilterOverlayVisible, onMobileFilterOverlayVisibleChange]);

	// Sync external trigger (parent's Filters button) with internal state
	useEffect(() => {
		setMobileFilterOverlayVisible(mobileFilterOverlayOpen);
	}, [mobileFilterOverlayOpen]);

	const [isSearchFocused, setIsSearchFocused] = useState(false);
	const [openFilterKey, setOpenFilterKey] = useState<FilterDropdownKey | null>(null);
	// Web-only: while a filter dropdown is open, temporarily widen its trigger so the
	// "Select All | Clear" actions row inside the popup is never clipped. Other filters
	// wrap onto the next line automatically via filtersRow's flexWrap.
	const isWebFilterOpen = useCallback(
		(key: FilterDropdownKey) => Platform.OS === 'web' && openFilterKey === key,
		[openFilterKey]
	);
	const [selectedSortMode, setSelectedSortMode] = useState<'BIGGEST_DELTA' | 'SPARC_CATEGORY'>(() => getKpiSortMode());

	const handleSortModeChange = useCallback((mode: 'BIGGEST_DELTA' | 'SPARC_CATEGORY') => {
		setSelectedSortMode(mode);
		setKpiSortMode(mode);
	}, []);


	// ─── Unified Date Selection State (single source of truth) ──────────────
	const [internalDateSelection, setInternalDateSelection] = useState<DateSelectionState>({ mode: 'SINGLE_DATE' });
	const dateSelection = dateSelectionProp ?? internalDateSelection;
	const setDateSelection = onDateSelectionChange ?? setInternalDateSelection;

	// ─── Draft date selection: only committed to parent on Apply ────────────
	const [draftDateSelection, setDraftDateSelection] = useState<DateSelectionState>(dateSelection);

	// ─── Time Frame pill state (separate from dateSelection) ────────────────
	const [internalSelectedTimeFrame, setInternalSelectedTimeFrame] = useState<TimeFrameOption | null>(null);
	const selectedTimeFrame = selectedTimeFrameProp ?? internalSelectedTimeFrame;
	const setSelectedTimeFrame = onSelectedTimeFrameChange ?? setInternalSelectedTimeFrame;


	// Multi-select draft arrays — initialise from full parent arrays (for cross-tab sync)
	// so that when the component remounts after a tab switch, all multi-selections are preserved.
	const [draftRegionMulti, setDraftRegionMulti] = useState<LHDropdownItem[]>(
		selectedRegionsProp && selectedRegionsProp.length > 0 ? selectedRegionsProp : (region ? [region] : [])
	);
	const [draftMarketMulti, setDraftMarketMulti] = useState<LHDropdownItem[]>(
		selectedMarketsProp && selectedMarketsProp.length > 0 ? selectedMarketsProp : (market ? [market] : [])
	);

	// "Committed" (last-applied) snapshots for region & market — needed because those
	// are propagated to the parent immediately (for location-cascade), which causes
	// their drafts to re-sync via useEffect, making vanilla draft-vs-prop comparison
	// always appear unchanged. We compare against these snapshots in hasChanges instead.
	const [appliedRegionMulti, setAppliedRegionMulti] = useState<LHDropdownItem[]>(
		selectedRegionsProp && selectedRegionsProp.length > 0 ? selectedRegionsProp : (region ? [region] : [])
	);
	const [appliedMarketMulti, setAppliedMarketMulti] = useState<LHDropdownItem[]>(
		selectedMarketsProp && selectedMarketsProp.length > 0 ? selectedMarketsProp : (market ? [market] : [])
	);
	// Same immediate-propagation issue applies to Location Type (no draft state exists for it;
	// onLocationTypeChange is called directly from the dropdown). Track the last-applied
	// snapshot so hasChanges can detect a pending, un-applied Location Type selection.
	const [appliedLocationTypes, setAppliedLocationTypes] = useState<LHDropdownItem[]>(selectedLocationTypes);
	const [appliedPeriodMulti, setAppliedPeriodMulti] = useState<LHDropdownItem[]>(
		selectedPeriodsProp && selectedPeriodsProp.length > 0 ? selectedPeriodsProp : (period ? [period] : [])
	);
	const [appliedWeekMulti, setAppliedWeekMulti] = useState<LHDropdownItem[]>(
		selectedWeeksProp && selectedWeeksProp.length > 0 ? selectedWeeksProp : (weekInYear ? [weekInYear] : [])
	);
	const [draftYearMulti, setDraftYearMulti] = useState<LHDropdownItem[]>(
		selectedYearsProp && selectedYearsProp.length > 0 ? selectedYearsProp : (year ? [year] : [])
	);
	const [draftPeriodMulti, setDraftPeriodMulti] = useState<LHDropdownItem[]>(
		selectedPeriodsProp && selectedPeriodsProp.length > 0 ? selectedPeriodsProp : (period ? [period] : [])
	);
	const [draftWeekMulti, setDraftWeekMulti] = useState<LHDropdownItem[]>(
		selectedWeeksProp && selectedWeeksProp.length > 0 ? selectedWeeksProp : (weekInYear ? [weekInYear] : [])
	);

	// ─── Year → Period → Week cascading ─────────────────────────────────────
	const selectedYearNumbers: number[] = useMemo(() => {
		return draftYearMulti
			.map(y => { const num = Number(String(y.id).replace(/\D/g, '')); return Number.isNaN(num) ? null : num; })
			.filter((n): n is number => n !== null);
	}, [draftYearMulti]);

	const selectedPeriodNumbers: number[] = useMemo(() => {
		return draftPeriodMulti
			.map(p => { const num = Number(String(p.id).replace(/\D/g, '')); return Number.isNaN(num) ? null : num; })
			.filter((n): n is number => n !== null);
	}, [draftPeriodMulti]);

	const { periodOptions: apiPeriodOptions, weekOptions: apiWeekOptions } = usePeriodWeeks(selectedYearNumbers, selectedPeriodNumbers);

	const effectivePeriodOptions: LHDropdownItem[] = useMemo(() => {
		if (selectedYearNumbers.length > 0 && apiPeriodOptions.length > 0) return apiPeriodOptions;
		return periodOptions;
	}, [selectedYearNumbers, apiPeriodOptions, periodOptions]);

	const effectiveWeekOptions: LHDropdownItem[] = useMemo(() => {
		// Always prefer API-derived week options when a year is selected — they contain
		// correct weekInYear IDs. Only fall back to static weekOptions if no year is selected.
		if (selectedYearNumbers.length > 0) return apiWeekOptions;
		return weekOptions;
	}, [selectedYearNumbers, apiWeekOptions, weekOptions]);

	// ─── Disable future periods/weeks when current year is sole selection ────
	const currentPeriodWeek = useSelector((state: RootState) => state.appData?.periodWeek?.data);
	const { disabledPeriodIds, disabledWeekIds } = useDisabledPeriodWeekIds({
		currentPeriodWeek,
		selectedYearNumbers,
		periodOptions: effectivePeriodOptions,
		weekOptions: effectiveWeekOptions,
	});

	// Derive all available period and weekInYear numbers for fallback when none are selected
	const allPeriods: number[] = useMemo(() => {
		return effectivePeriodOptions
			.map(p => { const num = Number(String(p.id).replace(/\D/g, '')); return Number.isNaN(num) ? null : num; })
			.filter((n): n is number => n !== null);
	}, [effectivePeriodOptions]);

	const allWeekInYears: number[] = useMemo(() => {
		return effectiveWeekOptions
			.map(w => { const num = Number(String(w.id).replace(/\D/g, '')); return Number.isNaN(num) ? null : num; })
			.filter((n): n is number => n !== null);
	}, [effectiveWeekOptions]);

	// ─── Available dates for DateFilterPicker (derived from Redux yearData) ──
	const { yearData: periodWeeksYearData } = useSelector(
		(state: RootState) => state.analyticsHub.periodWeeks
	);

	// ─── Single source of truth: default dashboard date selection (no date pre-selected) ──
	const createDefaultDateSelection = useCallback((): DateSelectionState => {
		return { mode: 'SINGLE_DATE' };
	}, []);

	const selectedWeekInYearNumbers: number[] = useMemo(() => {
		return draftWeekMulti
			.map(w => { const num = Number(String(w.id).replace(/\D/g, '')); return Number.isNaN(num) ? null : num; })
			.filter((n): n is number => n !== null);
	}, [draftWeekMulti]);

	const availableDates: string[] = useMemo(() => {
		return getAvailableDates(periodWeeksYearData, selectedWeekInYearNumbers, selectedPeriodNumbers);
	}, [periodWeeksYearData, selectedWeekInYearNumbers, selectedPeriodNumbers]);

	// ─── Time Frame key map ─────────────────────────────────────────────────
	const timeFrameKeyMap: Record<TimeFrameOption, TimeFrameKey> = useMemo(() => ({
		'week-to-date': 'WEEK_TO_DATE',
		'period-to-date': 'PERIOD_TO_DATE',
		'quarter-to-date': 'QUARTER_TO_DATE',
		'year-to-date': 'YEAR_TO_DATE',
		'last-closed-week': 'LAST_CLOSED_WEEK',
		'last-closed-period': 'LAST_CLOSED_PERIOD',
	}), []);

	const isDatePickerDisabled = draftYearMulti.length === 0;
	const datePlaceholder = availableDates.length === 0
		? 'No Dates Available'
		: 'Select Date';

	// ─── Helper: match dropdown options by constructing expected IDs ─────────
	const matchOptionsByValue = useCallback((
		options: LHDropdownItem[],
		values: number[],
		prefix: string
	): LHDropdownItem[] => {
		const idSet = new Set(values.map(v => `${prefix}${v}`));
		return options.filter(opt => idSet.has(String(opt.id)));
	}, []);

	// ─── Helper: build period items directly from yearData (avoids stale state) ──
	const buildPeriodItemsFromYearData = useCallback((periods: number[]): LHDropdownItem[] => {
		if (!periodWeeksYearData || periodWeeksYearData.length === 0) return [];
		const periodSet = new Set(periods);
		const items: LHDropdownItem[] = [];
		const seen = new Set<number>();
		for (const yd of periodWeeksYearData) {
			for (const pd of yd.periods) {
				if (periodSet.has(pd.period) && !seen.has(pd.period)) {
					seen.add(pd.period);
					items.push({ id: `P${pd.period}`, displayText: `P${pd.period}` });
				}
			}
		}
		return items.sort((a, b) => Number(String(a.id).replace(/\D/g, '')) - Number(String(b.id).replace(/\D/g, '')));
	}, [periodWeeksYearData]);

	// ─── Helper: build week items directly from yearData (avoids stale state) ──
	const buildWeekItemsFromYearData = useCallback((weekInYears: number[]): LHDropdownItem[] => {
		if (!periodWeeksYearData || periodWeeksYearData.length === 0) return [];
		const weekSet = new Set(weekInYears);
		const items: LHDropdownItem[] = [];
		const seen = new Set<number>();
		for (const yd of periodWeeksYearData) {
			for (const pd of yd.periods) {
				for (const weekObj of pd.weeks) {
					if (weekSet.has(weekObj.weekInYear) && !seen.has(weekObj.weekInYear)) {
						seen.add(weekObj.weekInYear);
						items.push({
							id: `W${weekObj.weekInYear}`,
							displayText: `W${weekObj.weekInYear} (P${pd.period}W${weekObj.week})`,
						});
					}
				}
			}
		}
		return items.sort((a, b) => Number(String(a.id).replace(/\D/g, '')) - Number(String(b.id).replace(/\D/g, '')));
	}, [periodWeeksYearData]);

	// ─── Stable refs for values declared later in the component (avoids forward-reference errors) ──
	// triggerFetch comes from useKpiOverview (below), effectiveLocationOptions is a useMemo (below).
	// Using refs lets handleTimeFrameSelect always access the latest value without hoisting issues.
	const triggerFetchRef = useRef<() => void>(() => {});
	const effectiveLocationOptionsRef = useRef<LHDropdownItem[]>([]);

	// ─── applyTimeFrameFilters: syncs Year/Period/Week/Date from a range ────
	const applyTimeFrameFilters = useCallback((range: { startDate: string; endDate: string; years: number[]; periods: number[]; weekInYears: number[] }, option: TimeFrameOption) => {
		// 1. Year — select all years in the range
		const yearItems = matchOptionsByValue(yearOptions, range.years, '');
		setDraftYearMulti(yearItems);
		setDraftYear(yearItems.length > 0 ? yearItems[yearItems.length - 1] : null);

		// 2. Period — build directly from yearData (not from effectivePeriodOptions which may be stale)
		const periodItems = buildPeriodItemsFromYearData(range.periods);
		setDraftPeriodMulti(periodItems);
		setDraftPeriod(periodItems.length > 0 ? periodItems[periodItems.length - 1] : null);

		// 3. Week — build directly from yearData (not from effectiveWeekOptions which is filtered by old periods)
		const weekItems = buildWeekItemsFromYearData(range.weekInYears);
		setDraftWeekMulti(weekItems);
		setDraftWeekInYear(weekItems.length > 0 ? weekItems[weekItems.length - 1] : null);

		// 4. Date selection — update draft only (committed on Apply)
		setDraftDateSelection(createDateRangeSelection(range.startDate, range.endDate, option));
	}, [yearOptions, matchOptionsByValue, buildPeriodItemsFromYearData, buildWeekItemsFromYearData]);

	// ─── restoreDefaultFilterState: reusable for Reset + toggle-off ─────────
	const restoreDefaultFilterState = useCallback(() => {
		const defaultYear = yearOptions.length > 0 ? yearOptions[0] : null;
		setSelectedTimeFrame(null);
		setDraftYearMulti(defaultYear ? [defaultYear] : []);
		setDraftYear(defaultYear);
		setDraftPeriodMulti([]);
		setDraftPeriod(null);
		setDraftWeekMulti([]);
		setDraftWeekInYear(null);
		if (onLocationTypeChange) { onLocationTypeChange(null); }

		// Single source of truth for default date selection
		const defaultDate = createDefaultDateSelection();
		setDraftDateSelection(defaultDate);
		setDateSelection(defaultDate);
	}, [yearOptions, createDefaultDateSelection, onLocationTypeChange]);

	// ─── handleTimeFrameSelect: toggle + sync dropdowns + auto-apply ────────
	const handleTimeFrameSelect = useCallback((option: TimeFrameOption) => {
		// Toggle: clicking the already-active pill deselects it
		if (selectedTimeFrame === option) {
			restoreDefaultFilterState();
			// Auto-apply reset: propagate default state and re-fetch
			const defaultYear = yearOptions.length > 0 ? yearOptions[0] : null;
			onRegionChange(draftRegionMulti);
			onMarketChange(draftMarketMulti);
			const locationToApply = draftLocation.length > 0 ? draftLocation : effectiveLocationOptionsRef.current;
			onLocationChange(locationToApply);
			onYearChange(defaultYear ? [defaultYear] : []);
			onPeriodChange(null);
			onWeekInYearChange(null);
			setAppliedRegionMulti(draftRegionMulti);
			setAppliedMarketMulti(draftMarketMulti);
			setAppliedPeriodMulti([]);
			setAppliedWeekMulti([]);
			setAppliedLocationTypes(selectedLocationTypes);
			const defaultDate = createDefaultDateSelection();
			setDateSelection(defaultDate);
			triggerFetchRef.current();
			onApplyFilters?.();
			return;
		}

		if (!periodWeeksYearData || periodWeeksYearData.length === 0) return;
		const tfKey = timeFrameKeyMap[option];
		if (!tfKey) return;
		const range = getTimeFrameDateRange(periodWeeksYearData, tfKey);
		if (!range) return;

		setSelectedTimeFrame(option);
		applyTimeFrameFilters(range, option);

		// ── Auto-apply: compute resolved filter values synchronously and trigger API ──
		// We cannot rely on the React state updates from applyTimeFrameFilters being
		// flushed yet, so we re-derive the same values here and propagate directly.
		const yearItems = matchOptionsByValue(yearOptions, range.years, '');
		const periodItems = buildPeriodItemsFromYearData(range.periods);
		const weekItems = buildWeekItemsFromYearData(range.weekInYears);
		const resolvedDateSelection = createDateRangeSelection(range.startDate, range.endDate, option);

		onRegionChange(draftRegionMulti);
		onMarketChange(draftMarketMulti);
		const locationToApply = draftLocation.length > 0 ? draftLocation : effectiveLocationOptionsRef.current;
		onLocationChange(locationToApply);
		onYearChange(yearItems.length > 0 ? yearItems[yearItems.length - 1] : null);
		// Pass the full multi-select arrays so the parent (AnalyticsHubDashBoard) stores
		// all selected periods/weeks in selectedPeriods/selectedWeeks state — which is used
		// for the One Pager payload and cross-tab sync. Passing only the last item caused
		// the parent to hold [P8] instead of [P7, P8], resulting in an incomplete API payload.
		onPeriodChange(periodItems);
		onWeekInYearChange(weekItems);
		setAppliedRegionMulti(draftRegionMulti);
		setAppliedMarketMulti(draftMarketMulti);
		setAppliedPeriodMulti(periodItems);
		setAppliedWeekMulti(weekItems);
		setAppliedLocationTypes(selectedLocationTypes);
		setDateSelection(resolvedDateSelection);
		triggerFetchRef.current();
		onApplyFilters?.();
	}, [selectedTimeFrame, periodWeeksYearData, timeFrameKeyMap, applyTimeFrameFilters, restoreDefaultFilterState, yearOptions, draftRegionMulti, draftMarketMulti, draftLocation, selectedLocationTypes, onRegionChange, onMarketChange, onLocationChange, onYearChange, onPeriodChange, onWeekInYearChange, matchOptionsByValue, buildPeriodItemsFromYearData, buildWeekItemsFromYearData, createDefaultDateSelection, setDateSelection, setAppliedPeriodMulti, setAppliedWeekMulti, onApplyFilters]);

	// ─── clearActiveTimeFrame: mutual-exclusivity guard for manual Year/Period/Week/Date edits ──
	// Time Frame pre-population of Year/Period/Week/Date (handleTimeFrameSelect →
	// applyTimeFrameFilters, above) remains completely unchanged and does NOT call this.
	// This guard is invoked only from the user-facing dropdown/date-picker handlers below,
	// so it never fires as a side effect of the pill's own programmatic updates and cannot
	// create an update loop. Once a Time Frame is active, a manual Year/Period/Week/Date
	// edit means the user switched to a custom selection — so the pill is deselected.
	const clearActiveTimeFrame = useCallback(() => {
		if (selectedTimeFrame) {
			setSelectedTimeFrame(null);
			// Reset both the draft and committed date state atomically with the pill clear.
			// Without this, the DATE_RANGE values set by the time frame remain in the
			// parent's committed dateSelection (flowing into the API as startDate/endDate)
			// while the period/year/week selections have moved on — causing 0 results.
			const defaultDate = createDefaultDateSelection();
			setDraftDateSelection(defaultDate);
			setDateSelection(defaultDate);
		}
	}, [selectedTimeFrame, setSelectedTimeFrame, createDefaultDateSelection, setDraftDateSelection, setDateSelection]);

	// ─── Date picker change handler (updates draft only — committed on Apply) ─────────
	// Manually picking a date is a manual override too — clear any active Time Frame pill.
	const handleDateSelectionChange = useCallback((newState: DateSelectionState) => {
		clearActiveTimeFrame();
		setDraftDateSelection(newState);
	}, [clearActiveTimeFrame]);

	// Reset period when year changes (skip when a time frame pill is active — it manages its own state)
	useEffect(() => {
		if (selectedTimeFrame) return; // Time frame pill manages period/week selection
		if (selectedYearNumbers.length > 0 && apiPeriodOptions.length > 0 && draftPeriodMulti.length > 0) {
			const validPeriods = draftPeriodMulti.filter(p => apiPeriodOptions.some(opt => String(opt.id) === String(p.id)));
			if (validPeriods.length !== draftPeriodMulti.length) {
				setDraftPeriodMulti(validPeriods);
				setDraftPeriod(validPeriods.length > 0 ? validPeriods[validPeriods.length - 1] : null);
			}
		}
	}, [apiPeriodOptions, selectedYearNumbers, selectedTimeFrame]);

	// Reset week when period changes (skip when a time frame pill is active)
	useEffect(() => {
		if (selectedTimeFrame) return; // Time frame pill manages period/week selection
		if (apiWeekOptions.length > 0 && draftWeekMulti.length > 0) {
			const validWeeks = draftWeekMulti.filter(w => apiWeekOptions.some(opt => String(opt.id) === String(w.id)));
			if (validWeeks.length !== draftWeekMulti.length) {
				setDraftWeekMulti(validWeeks);
				setDraftWeekInYear(validWeeks.length > 0 ? validWeeks[validWeeks.length - 1] : null);
			}
		}
	}, [apiWeekOptions, selectedTimeFrame]);

	// Auto-sync dateSelection: only clear an invalid single-date selection.
	// Does NOT auto-pick a date on load — the DATE field starts empty.
	useEffect(() => {
		if (dateSelection.mode === 'DATE_RANGE') return; // Don't override time frame ranges
		// Only act if user had previously selected a date that's no longer valid
		if (dateSelection.selectedDate) {
			if (availableDates.length > 0 && !availableDates.includes(dateSelection.selectedDate)) {
				setDateSelection({ mode: 'SINGLE_DATE' });
			}
		}
	}, [availableDates, dateSelection]);

	// ─── Region → Markets fetching ──────────────────────────────────────────
	const selectedRegionIds = useMemo(() => {
		return draftRegionMulti.map(r => String(r.id).trim()).filter(id => id.toLowerCase() !== 'all' && id !== '');
	}, [draftRegionMulti]);

	const { combinedMarkets } = useRegionMarkets(selectedRegionIds);

	const regionDerivedMarketOptions: LHDropdownItem[] = useMemo(() => {
		if (selectedRegionIds.length === 0) return marketOptions;
		return combinedMarkets;
	}, [selectedRegionIds, combinedMarkets, marketOptions]);

	// ─── Derive API date range from DateSelectionState (single transformation) ──
	const { startDate: apiStartDate, endDate: apiEndDate } = getApiDateRange(dateSelection);

	// ─── Report date range to parent (for sibling tabs like Location Details) ──
	useEffect(() => {
		onDateRangeChange?.(apiStartDate, apiEndDate);
	}, [apiStartDate, apiEndDate, onDateRangeChange]);

	// ─── KPI Data Hook ──────────────────────────────────────────────────────
	const {
		metrics,
		loading,
		error,
		fetchKpis,
		filteredMetrics,
		topOpportunities,
		biggestWins,
		categoryCounts,
		searchTerm,
		setSearchTerm,
		selectedCategories,
		setSelectedCategories,
		toggleCategory,
		locationListToken,
		selectedYearToken,
		selectedPeriodToken,
		selectedWeekToken,
		triggerFetch,
	} = useKpiOverview({
		locationOptions,
		location,
		year,
		period,
		weekInYear,
		showDashboardContent,
		draftYearMulti,
		draftPeriodMulti,
		draftWeekMulti,
		allPeriods,
		allWeekInYears,
		startDate: apiStartDate,
		endDate: apiEndDate,
	});

	// ─── Report Config → KPI tag matching ───────────────────────────────────
	const reportConfigRows = useSelector((state: RootState) => state.reportingConfiguration.rows);

	const getLinkedReport = useCallback((metric: KpiMetric): LinkedReportInfo | null => {
		if (!reportConfigRows || reportConfigRows.length === 0) return null;
		const metricNameNormalized = metric.name.trim().toLowerCase();
		for (const row of reportConfigRows) {
			if (String(row.status).toLowerCase() !== 'active') continue;
			const tags = Array.isArray(row.kpiTags) ? row.kpiTags : [];
			const matched = tags.some(tag => {
				const tagNormalized = tag.trim().toLowerCase();
				return tagNormalized === metricNameNormalized || metricNameNormalized.includes(tagNormalized) || tagNormalized.includes(metricNameNormalized);
			});
			if (matched) {
				const link = (row.link && row.link.trim()) || 'TBD';
				return { reportName: row.name || '', reportLink: link, platform: inferPlatformFromLink(link) };
			}
		}
		return null;
	}, [reportConfigRows]);

	// ─── Filter change detection ────────────────────────────────────────────
	const hasChanges = useMemo(() => {
		// Region and market are immediately propagated to the parent for cascading, so their
		// draft values re-sync with the parent prop via useEffect — making draft-vs-prop
		// comparisons always look equal. Compare against the last-applied snapshots instead.
		const regionChanged = JSON.stringify(draftRegionMulti.map(r => String(r.id))) !==
			JSON.stringify(appliedRegionMulti.map(r => String(r.id)));
		const marketChanged = JSON.stringify(draftMarketMulti.map(m => String(m.id))) !==
			JSON.stringify(appliedMarketMulti.map(m => String(m.id)));

		// Location Type also propagates immediately (no draft state), so compare against
		// its last-applied snapshot the same way as region/market.
		const locationTypeChanged = JSON.stringify(selectedLocationTypes.map(lt => String(lt.id))) !==
			JSON.stringify(appliedLocationTypes.map(lt => String(lt.id)));

		// Compare draft date selection vs committed dateSelection
		const dateChanged = JSON.stringify(draftDateSelection) !== JSON.stringify(dateSelection);

		const yearChanged = JSON.stringify(draftYearMulti.map(y => String(y.id))) !==
			JSON.stringify((selectedYearsProp ?? []).map(y => String(y.id)));

		const periodChanged = JSON.stringify(draftPeriodMulti.map(p => String(p.id))) !==
			JSON.stringify(appliedPeriodMulti.map(p => String(p.id)));

		const weekChanged = JSON.stringify(draftWeekMulti.map(w => String(w.id))) !==
			JSON.stringify(appliedWeekMulti.map(w => String(w.id)));

		return regionChanged || marketChanged || locationTypeChanged || dateChanged || yearChanged ||
			periodChanged || weekChanged ||
			JSON.stringify(draftLocation) !== JSON.stringify(location);
	}, [draftRegionMulti, appliedRegionMulti, draftMarketMulti, appliedMarketMulti, selectedLocationTypes, appliedLocationTypes, draftLocation, location, draftYearMulti, selectedYearsProp, draftPeriodMulti, appliedPeriodMulti, draftWeekMulti, appliedWeekMulti, draftDateSelection, dateSelection]);

	// ─── Layout ─────────────────────────────────────────────────────────────
	const isIpad = Platform.OS === 'ios' && Math.min(width, height) >= 768 && Math.max(width, height) <= 1366;
	const isIpadPortrait = width >= 768 && width <= 1024 && height > width;
	const isMobileIos = Platform.OS === 'ios' && width < 768;
	const isMobileNative = isMobileNativeView;

	const columns = useMemo(() => {
		if (isIpad) return isIpadPortrait ? 2 : 3;
		if (width >= 1024) return 4;
		if (width >= 850) return 2;
		return 1;
	}, [isIpad, isIpadPortrait, width]);

	// ─── Sync drafts with parent props ──────────────────────────────────────
	useEffect(() => { setDraftRegion(region); }, [region]);
	useEffect(() => { setDraftMarket(market); }, [market]);
	useEffect(() => { setDraftLocation(location); }, [location]);
	useEffect(() => { setDraftYear(year); }, [year]);
	useEffect(() => { setDraftPeriod(period); }, [period]);
	useEffect(() => { setDraftWeekInYear(weekInYear); }, [weekInYear]);

	// Reset market when region changes
	const prevRegionIdRef = useRef<string | number | undefined>(region?.id);
	const prevMarketIdRef = useRef<string | number | undefined>(market?.id);

	useEffect(() => {
		if (prevRegionIdRef.current !== region?.id && region != null) {
			setDraftMarket(null);
			setDraftMarketMulti([]);
			onMarketChange(null);
		}
		prevRegionIdRef.current = region?.id;
	}, [region?.id, onMarketChange]);

	useEffect(() => {
		if (prevMarketIdRef.current !== market?.id && market != null) {
			setDraftLocation([]);
			onLocationChange([]);
		}
		prevMarketIdRef.current = market?.id;
	}, [market?.id, onLocationChange]);

	// ─── Handlers ───────────────────────────────────────────────────────────
	const handleApplyFilters = () => {
		// When no specific locations are selected, send all currently filtered (visible)
		// locations as the payload — i.e. every location in the selected region / market.
		const locationToApply = draftLocation.length > 0 ? draftLocation : effectiveLocationOptions;
		// Propagate region to the parent. On desktop the dropdown already calls
		// onRegionChange immediately, so this is a harmless no-op. On mobile the
		// overlay only updates local drafts, so this ensures the parent's
		// selectedRegions is up-to-date for cross-tab sync. The subsequent
		// onMarketChange / onLocationChange calls override the market/location
		// clearing side-effects of onRegionChange thanks to React state batching.
		onRegionChange(draftRegionMulti);
		onMarketChange(draftMarketMulti);
		onLocationChange(locationToApply);
		onYearChange(draftYearMulti);
		onPeriodChange(draftPeriodMulti);
		onWeekInYearChange(draftWeekMulti);
		// Advance the committed snapshots so hasChanges resets to false after Apply.
		setAppliedRegionMulti(draftRegionMulti);
		setAppliedMarketMulti(draftMarketMulti);
		setAppliedPeriodMulti(draftPeriodMulti);
		setAppliedWeekMulti(draftWeekMulti);
		setAppliedLocationTypes(selectedLocationTypes);
		// Commit draft date selection to the parent (triggers API via dateSelection → getApiDateRange)
		setDateSelection(draftDateSelection);
		// Request fetch after Apply updates are staged.
		triggerFetch();
		// Notify consumers (e.g. One Pager V2) that filters were just committed.
		onApplyFilters?.();
	};

	const handleResetFilters = () => {
		restoreDefaultFilterState();
		setDraftRegion(null); setDraftMarket(null); setDraftLocation([]);
		setDraftRegionMulti([]); setDraftMarketMulti([]);
		setAppliedRegionMulti([]); setAppliedMarketMulti([]);
		setAppliedPeriodMulti([]); setAppliedWeekMulti([]);
		setAppliedLocationTypes([]);
		triggerFetch();
		onRegionChange(null); onMarketChange(null); onLocationChange([]);
		onYearChange(yearOptions.length > 0 ? [yearOptions[0]] : []); onPeriodChange(null); onWeekInYearChange(null);
		// Notify consumers (e.g. One Pager V2) that filters were just reset.
		onApplyFilters?.();
	};

	const openMetric = useCallback((metric: KpiMetric) => { setSelectedKpi(metric); setModalVisible(true); }, []);
	const closeModal = useCallback(() => { setModalVisible(false); setSelectedKpi(null); }, []);
	const toggleWins = useCallback(() => { setShowWins(prev => !prev); }, []);

	// ─── Options (strip "All") ──────────────────────────────────────────────
	const effectiveRegionOptions = useMemo(() => regionOptions.filter(o => String(o.id).trim().toLowerCase() !== 'all'), [regionOptions]);
	const effectiveMarketOptions = useMemo(() => marketOptions.filter(o => String(o.id).trim().toLowerCase() !== 'all'), [marketOptions]);
	const effectiveLocationOptions = useMemo(() => locationOptions.filter(o => String(o.id).trim().toLowerCase() !== 'all'), [locationOptions]);

	// ─── Keep forward-reference refs in sync every render ───────────────────
	triggerFetchRef.current = triggerFetch;
	effectiveLocationOptionsRef.current = effectiveLocationOptions;

	const isAllChipActive = selectedCategories.length === 0;

	// ─── SPARC Category Grouping ────────────────────────────────────────────
	const sparcGroupedMetrics = useMemo(() => {
		return KPI_CATEGORY_FILTER_ORDER
			.map(category => ({
				category,
				metrics: filteredMetrics.filter(m => resolveKpiCategory(m.category) === category),
			}))
			.filter(group => group.metrics.length > 0);
	}, [filteredMetrics]);

	// ─── Render helpers ─────────────────────────────────────────────────────
	const renderBiggestDelta = () => (
		<>
			<KPISection
				title="TOP OPPORTUNITIES"
				countLabel={`${topOpportunities.length} METRICS`}
				metrics={topOpportunities}
				emptyMessage="All metrics are meeting or exceeding target."
				columns={columns}
				onOpenMetric={openMetric}
				searchTerm={searchTerm}
				getLinkedReport={getLinkedReport}
				onNavigateToReports={onNavigateToReports}
			/>

			<View style={[styles.winsSectionWrapper, isDark && { borderColor: colors.border.default }]}>
					<View style={[styles.winsBanner, isMobileIos && styles.winsBannerMobile, isDark && { backgroundColor: colors.background.elevated }]}>
						<View style={[styles.winsBannerTitleRow, isMobileIos && styles.winsBannerTitleRowMobile]}>
							<TouchableOpacity style={styles.winsBannerTitleLeft} onPress={toggleWins} accessibilityRole="button" accessibilityLabel={showWins ? 'Hide biggest wins section' : 'Show biggest wins section'}>
								<MaterialIcons name="star" size={20} color="#F0A500" />
								<Text style={[styles.winsBannerTitle, isDark && { color: colors.text.primary }]}>Biggest Wins</Text>
							</TouchableOpacity>
							<View style={styles.winsBannerRightControls}>
								<View style={styles.winsCountPill}>
									<Text style={styles.winsCountPillText}>{biggestWins.length} metrics</Text>
								</View>
								{!isMobileIos && (
									<TouchableOpacity onPress={toggleWins} style={[styles.winsToggleBtn, isDark && { borderColor: '#6FD98A' }]} accessibilityRole="button" accessibilityLabel={showWins ? 'Hide biggest wins section' : 'Show biggest wins section'}>
										<Text style={[styles.winsToggleBtnText, isDark && { color: '#6FD98A' }]}>{showWins ? 'Hide Wins' : 'Show Wins'}</Text>
										<MaterialIcons name={showWins ? 'expand-less' : 'expand-more'} size={18} color={isDark ? '#6FD98A' : '#198038'} />
									</TouchableOpacity>
								)}
							</View>
						</View>
						<Text style={[styles.winsBannerSubtitle, isDark && { color: colors.text.secondary }]}>These metrics are beating target — click to expand and celebrate your team's performance</Text>
						{isMobileIos && (
							<View style={styles.winsToggleRow}>
								<TouchableOpacity onPress={toggleWins} style={[styles.winsToggleBtn, styles.winsToggleBtnMobile, isDark && { borderColor: '#6FD98A' }]} accessibilityRole="button" accessibilityLabel={showWins ? 'Hide biggest wins section' : 'Show biggest wins section'}>
									<Text style={[styles.winsToggleBtnText, isDark && { color: '#6FD98A' }]}>{showWins ? 'Hide Wins' : 'Show Wins'}</Text>
									<MaterialIcons name={showWins ? 'expand-less' : 'expand-more'} size={18} color={isDark ? '#6FD98A' : '#198038'} />
								</TouchableOpacity>
							</View>
						)}
					</View>
					{showWins ? (
						biggestWins.length === 0 ? (
							<View style={[styles.winsCardsArea, isDark && { backgroundColor: colors.background.surface }]}>
								<EmptyState message="No metrics are currently beating target." />
							</View>
						) : (
							<View style={[styles.winsCardsArea, isDark && { backgroundColor: colors.background.surface }]}>
							<View style={styles.cardsGrid}>
								{biggestWins.map(item => (
									<View key={`${item.name}-${item.category}`} style={[styles.cardColumn, { width: `${100 / columns}%` }]}>
										<KPICard
											metric={item}
											onOpen={openMetric}
											searchTerm={searchTerm}
											linkedReport={getLinkedReport(item)}
											onNavigateToReports={onNavigateToReports}
										/>
									</View>
								))}
							</View>
						</View>
					)
				) : null}
			</View>
		</>
	);

	const renderSPARCCategories = () => (
		<>
			{sparcGroupedMetrics.map(group => {
				const categoryColors = isDark ? KPI_CATEGORY_COLORS_DARK[group.category] : KPI_CATEGORY_COLORS[group.category];
				const metricCount = group.metrics.length;
				return (
					<View key={group.category} style={styles.sparcGroupContainer}>
						<View style={styles.sparcGroupHeader}>
							<View style={[styles.sparcGroupDot, { backgroundColor: categoryColors.icon }]} />
							<Text style={[styles.sparcGroupTitle, isDark && { color: colors.text.primary }]}>{group.category.toUpperCase()}</Text>
							<Text style={[styles.sparcGroupBadge, isDark && { color: colors.text.secondary }]}>
								{metricCount} {metricCount === 1 ? 'metrics' : 'metrics'}
							</Text>
						</View>
						<View style={styles.cardsGrid}>
							{group.metrics.map(item => (
								<View key={`${item.name}-${item.category}`} style={[styles.cardColumn, { width: `${100 / columns}%` }]}>
									<KPICard
										metric={item}
										onOpen={openMetric}
										searchTerm={searchTerm}
										linkedReport={getLinkedReport(item)}
										onNavigateToReports={onNavigateToReports}
									/>
								</View>
							))}
						</View>
					</View>
				);
			})}
		</>
	);

	// ─── Mobile filter overlay ──────────────────────────────────────────────
	if (isMobileNative && isMobileFilterOverlayVisible) {
		return (
			<View style={styles.mobileOverlayContainer}>

				{/* Filter rows */}
				<ScrollView style={styles.mobileOverlayBody} contentContainerStyle={styles.mobileOverlayBodyContent} keyboardShouldPersistTaps="handled">
					<View style={styles.mobileOverlayFilterRow}>
						<Text style={styles.mobileOverlayFilterLabel}>REGION / UNIT</Text>
						<KPIFilterDropdown childList={effectiveRegionOptions} selectedValue={draftRegionMulti} setSelectedValue={(value) => { setDraftRegionMulti(value); setDraftRegion(value.length > 0 ? value[value.length - 1] : null); onRegionChange(value); }} placeholder="All" isOpen={openFilterKey === 'region'} onOpenChange={(open) => setOpenFilterKey(open ? 'region' : null)} testID="kpi-mobile-filter-region" autoFocusSearchOnOpen />
					</View>

					<View style={styles.mobileOverlayFilterRow}>
						<Text style={styles.mobileOverlayFilterLabel}>MARKET / POD</Text>
						<KPIFilterDropdown childList={regionDerivedMarketOptions} selectedValue={draftMarketMulti} setSelectedValue={(value) => { setDraftMarketMulti(value); setDraftMarket(value.length > 0 ? value[value.length - 1] : null); onMarketChange(value); }} placeholder="All" readOnly={isMarketDisabled} isOpen={openFilterKey === 'market'} onOpenChange={(open) => setOpenFilterKey(open ? 'market' : null)} testID="kpi-mobile-filter-market" autoFocusSearchOnOpen />
					</View>

					<View style={styles.mobileOverlayFilterRow}>
						<Text style={styles.mobileOverlayFilterLabel}>LOCATION</Text>
						<KPIFilterDropdown childList={effectiveLocationOptions} selectedValue={draftLocation} setSelectedValue={(value) => { setDraftLocation(value); }} placeholder="All" readOnly={isLocationDisabled} isOpen={openFilterKey === 'location'} onOpenChange={(open) => setOpenFilterKey(open ? 'location' : null)} testID="kpi-mobile-filter-location" autoFocusSearchOnOpen sortItems />
					</View>

					<View style={styles.mobileOverlayFilterRow}>
						<Text style={styles.mobileOverlayFilterLabel}>LOCATION TYPE</Text>
						<KPIFilterDropdown
							childList={locationTypeOptions}
							selectedValue={selectedLocationTypes}
							setSelectedValue={(value) => { if (onLocationTypeChange) { onLocationTypeChange(value); } }}
							placeholder="All"
							isOpen={openFilterKey === 'locationType'}
							onOpenChange={(open) => setOpenFilterKey(open ? 'locationType' : null)}
							testID="kpi-mobile-filter-location-type"
							autoFocusSearchOnOpen
						/>
					</View>

					<View style={styles.mobileOverlayFilterRow}>
						<Text style={styles.mobileOverlayFilterLabel}>TIME FRAME</Text>
						<View style={styles.mobileOverlayTimeFrameWrap}>
							<TimeFrameSelector
								selectedTimeFrame={selectedTimeFrame}
								onSelect={handleTimeFrameSelect}
								useMobileAbbreviations
								hideLabel
							/>
						</View>
					</View>

					<View style={styles.mobileOverlayFilterRow}>
						<Text style={styles.mobileOverlayFilterLabel}>YEAR</Text>
						<KPIFilterDropdown childList={yearOptions} selectedValue={draftYearMulti} setSelectedValue={(value) => { clearActiveTimeFrame(); setDraftYearMulti(value); setDraftYear(value.length > 0 ? value[value.length - 1] : null); }} placeholder="All" isOpen={openFilterKey === 'year'} onOpenChange={(open) => setOpenFilterKey(open ? 'year' : null)} testID="kpi-mobile-filter-year" autoFocusSearchOnOpen />
					</View>

					<View style={styles.mobileOverlayFilterRow}>
						<Text style={styles.mobileOverlayFilterLabel}>PERIOD</Text>
						<KPIFilterDropdown childList={effectivePeriodOptions} selectedValue={draftPeriodMulti} setSelectedValue={(value) => { clearActiveTimeFrame(); setDraftPeriodMulti(value); setDraftPeriod(value.length > 0 ? value[value.length - 1] : null); }} placeholder="All" isOpen={openFilterKey === 'period'} onOpenChange={(open) => setOpenFilterKey(open ? 'period' : null)} testID="kpi-mobile-filter-period" autoFocusSearchOnOpen disabledIds={disabledPeriodIds} />
					</View>

					<View style={styles.mobileOverlayFilterRow}>
						<Text style={styles.mobileOverlayFilterLabel}>WEEK IN YEAR</Text>
						<KPIFilterDropdown childList={effectiveWeekOptions} selectedValue={draftWeekMulti} setSelectedValue={(value) => { clearActiveTimeFrame(); setDraftWeekMulti(value); setDraftWeekInYear(value.length > 0 ? value[value.length - 1] : null); }} placeholder="All" isOpen={openFilterKey === 'week'} onOpenChange={(open) => setOpenFilterKey(open ? 'week' : null)} testID="kpi-mobile-filter-week" autoFocusSearchOnOpen disabledIds={disabledWeekIds} />
					</View>

					<View style={styles.mobileOverlayFilterRow}>
						<Text style={styles.mobileOverlayFilterLabel}>CALENDAR</Text>
						<DateFilterPicker
							value={draftDateSelection}
							availableDates={availableDates}
							onChange={handleDateSelectionChange}
							disabled={isDatePickerDisabled}
							placeholder={datePlaceholder}
							testID="kpi-mobile-filter-date"
						/>
					</View>
				</ScrollView>

				{/* Action buttons */}
				<View style={styles.mobileOverlayFooter}>
					<TouchableOpacity style={styles.mobileOverlayCancelBtn} onPress={() => setMobileFilterOverlayVisible(false)} accessibilityRole="button" accessibilityLabel="Close filters">
						<Text style={styles.mobileOverlayCancelBtnText}>Close</Text>
					</TouchableOpacity>
					<TouchableOpacity style={styles.mobileOverlayResetBtn} onPress={() => { handleResetFilters(); setMobileFilterOverlayVisible(false); }} accessibilityRole="button" accessibilityLabel="Reset filters">
						<Text style={styles.mobileOverlayResetBtnText}>Reset</Text>
					</TouchableOpacity>
					<TouchableOpacity style={styles.mobileOverlayApplyBtn} onPress={() => { handleApplyFilters(); setMobileFilterOverlayVisible(false); }} accessibilityRole="button" accessibilityLabel="Apply filters">
						<Text style={styles.mobileOverlayApplyBtnText}>Apply</Text>
					</TouchableOpacity>
				</View>
			</View>
		);
	}

	// ─── Render ─────────────────────────────────────────────────────────────
	return (
		<View style={styles.contentContainer}>
			{isMobileNative && !hideMobileFilterButton ? (
				<View
					style={[
						styles.mobileFilterTriggerRow,
						mobileFilterButtonRightAligned && styles.mobileFilterTriggerRowRightAligned,
					]}
				>
					{onCustomizeKpisPress && (
						<TouchableOpacity style={styles.customizeKpisButton} onPress={onCustomizeKpisPress} accessibilityRole="button" accessibilityLabel="Customize KPIs">
							<MaterialIcons name="tune" size={18} color="#0B5FC6" />
							<Text style={styles.customizeKpisButtonText}>Customize KPIs</Text>
						</TouchableOpacity>
					)}
					<TouchableOpacity
						style={[
							styles.mobileFilterButton,
							mobileFilterButtonRightAligned && styles.mobileFilterButtonRightSpacing,
						]}
						onPress={() => setMobileFilterOverlayVisible(true)}
						accessibilityRole="button"
						accessibilityLabel="Open filters"
					>
						<MaterialIcons name="filter-list" size={20} color="#FFFFFF" />
						<Text style={styles.mobileFilterButtonText}>Filter</Text>
					</TouchableOpacity>
				</View>
			) : isMobileNative && hideMobileFilterButton ? (
				// Parent provides its own Filters button above the tabs —
				// don't render the inline filter panel; the full-screen overlay
				// (rendered above this block) handles filter UI when triggered.
				null
			) : (
			<>
			<View style={[styles.filtersContainer, isDark && { backgroundColor: colors.background.surface, borderColor: colors.border.default }]}>
			<View style={styles.filtersRow}>
				<View style={[styles.filterItem, isIpadPortrait && styles.filterItemIpadPortrait, isWebFilterOpen('region') && styles.filterItemExpanded]}>
					<Text style={[styles.filterLabel, isDark && { color: colors.text.secondary }]}>REGION / UNIT</Text>
					<KPIFilterDropdown childList={effectiveRegionOptions} selectedValue={draftRegionMulti} setSelectedValue={(value) => { setDraftRegionMulti(value); setDraftRegion(value.length > 0 ? value[value.length - 1] : null); onRegionChange(value); }} placeholder="All" isOpen={openFilterKey === 'region'} onOpenChange={(open) => setOpenFilterKey(open ? 'region' : null)} testID="kpi-filter-region" autoFocusSearchOnOpen />
				</View>
				<View style={[styles.filterItem, isIpadPortrait && styles.filterItemIpadPortrait, isWebFilterOpen('market') && styles.filterItemExpanded]}>
					<Text style={[styles.filterLabel, isDark && { color: colors.text.secondary }]}>MARKET / POD</Text>
					<KPIFilterDropdown childList={regionDerivedMarketOptions} selectedValue={draftMarketMulti} setSelectedValue={(value) => { setDraftMarketMulti(value); setDraftMarket(value.length > 0 ? value[value.length - 1] : null); onMarketChange(value); }} placeholder="All" readOnly={isMarketDisabled} isOpen={openFilterKey === 'market'} onOpenChange={(open) => setOpenFilterKey(open ? 'market' : null)} testID="kpi-filter-market" autoFocusSearchOnOpen />
				</View>
				<View style={[styles.filterItem, isIpadPortrait && styles.filterItemIpadPortrait, isWebFilterOpen('location') && styles.filterItemExpanded]}>
					<Text style={[styles.filterLabel, isDark && { color: colors.text.secondary }]}>LOCATION</Text>
					<KPIFilterDropdown childList={effectiveLocationOptions} selectedValue={draftLocation} setSelectedValue={(value) => { setDraftLocation(value); }} placeholder="All" readOnly={isLocationDisabled} isOpen={openFilterKey === 'location'} onOpenChange={(open) => setOpenFilterKey(open ? 'location' : null)} testID="kpi-filter-location" autoFocusSearchOnOpen sortItems />
				</View>
				<View style={[styles.filterItemSmall, isIpadPortrait && styles.filterItemIpadPortrait, isWebFilterOpen('locationType') && styles.filterItemExpanded]}>
					<Text style={[styles.filterLabel, isDark && { color: colors.text.secondary }]}>LOCATION TYPE</Text>
					<KPIFilterDropdown childList={locationTypeOptions} selectedValue={selectedLocationTypes} setSelectedValue={(value) => { if (onLocationTypeChange) { onLocationTypeChange(value); } }} placeholder="All" isOpen={openFilterKey === 'locationType'} onOpenChange={(open) => setOpenFilterKey(open ? 'locationType' : null)} testID="kpi-filter-location-type" autoFocusSearchOnOpen />
				</View>
				<View style={[styles.filterItemSmall, isIpadPortrait && styles.filterItemIpadPortrait, isWebFilterOpen('year') && styles.filterItemExpanded]}>
					<Text style={[styles.filterLabel, isDark && { color: colors.text.secondary }]}>YEAR</Text>
					<KPIFilterDropdown childList={yearOptions} selectedValue={draftYearMulti} setSelectedValue={(value) => { clearActiveTimeFrame(); setDraftYearMulti(value); setDraftYear(value.length > 0 ? value[value.length - 1] : null); }} placeholder="All" isOpen={openFilterKey === 'year'} onOpenChange={(open) => setOpenFilterKey(open ? 'year' : null)} testID="kpi-filter-year" autoFocusSearchOnOpen />
				</View>
				<View style={[styles.filterItemSmall, isIpadPortrait && styles.filterItemIpadPortrait, isWebFilterOpen('period') && styles.filterItemExpanded]}>
					<Text style={[styles.filterLabel, isDark && { color: colors.text.secondary }]}>PERIOD</Text>
					<KPIFilterDropdown childList={effectivePeriodOptions} selectedValue={draftPeriodMulti} setSelectedValue={(value) => { clearActiveTimeFrame(); setDraftPeriodMulti(value); setDraftPeriod(value.length > 0 ? value[value.length - 1] : null); }} placeholder="All" isOpen={openFilterKey === 'period'} onOpenChange={(open) => setOpenFilterKey(open ? 'period' : null)} testID="kpi-filter-period" autoFocusSearchOnOpen disabledIds={disabledPeriodIds} />
				</View>
				<View style={[styles.filterItemSmall, isIpadPortrait && styles.filterItemIpadPortrait, isWebFilterOpen('week') && styles.filterItemExpanded]}>
					<Text style={[styles.filterLabel, isDark && { color: colors.text.secondary }]}>WEEK IN YEAR</Text>
					<KPIFilterDropdown childList={effectiveWeekOptions} selectedValue={draftWeekMulti} setSelectedValue={(value) => { clearActiveTimeFrame(); setDraftWeekMulti(value); setDraftWeekInYear(value.length > 0 ? value[value.length - 1] : null); }} placeholder="All" isOpen={openFilterKey === 'week'} onOpenChange={(open) => setOpenFilterKey(open ? 'week' : null)} testID="kpi-filter-week" autoFocusSearchOnOpen disabledIds={disabledWeekIds} />
				</View>
				<View style={[styles.filterItemSmall, isIpadPortrait && styles.filterItemIpadPortrait]}>
					<Text style={[styles.filterLabel, isDark && { color: colors.text.secondary }]}>DATE</Text>
					<DateFilterPicker
						value={draftDateSelection}
						availableDates={availableDates}
						onChange={handleDateSelectionChange}
						disabled={isDatePickerDisabled}
						placeholder={datePlaceholder}
						testID="kpi-filter-date"
					/>
				</View>
				<View style={styles.filterActionsInline}>
					<TouchableOpacity style={styles.resetFiltersBtn} onPress={handleResetFilters} accessibilityRole="button" accessibilityLabel="Reset filters">
						<Text style={styles.resetFiltersBtnText}>Reset</Text>
					</TouchableOpacity>
					<TouchableOpacity style={[styles.applyFiltersBtn, !hasChanges && styles.applyFiltersBtnDisabled]} onPress={handleApplyFilters} accessibilityRole="button" accessibilityLabel="Apply filters" disabled={!hasChanges}>
						<Text style={[styles.applyFiltersBtnText, !hasChanges && styles.applyFiltersBtnTextDisabled]}>Apply</Text>
					</TouchableOpacity>
					{/* <View style={styles.customViewsWrap}>
						<CustomViewsButton />
					</View> */}
				</View>
			</View>

			{/* Horizontal divider */}
			<View style={styles.filterDivider} />

			{/* Time Frame section */}
			<View style={styles.timeFrameRow}>
				<TimeFrameSelector selectedTimeFrame={selectedTimeFrame} onSelect={handleTimeFrameSelect} />
			</View>
			</View>
			</>
			)}

			{showDashboardContent ? (
				<View>
					{loading ? (
						<KPIOverviewLoadingSkeleton columns={columns} />
					) : error ? (
						<View style={styles.centerState}>
							<Text style={styles.errorText}>{error}</Text>
							<TouchableOpacity style={styles.retryButton} onPress={fetchKpis} accessibilityRole="button" accessibilityLabel="Retry KPI overview API call">
								<Text style={styles.retryButtonText}>Retry</Text>
							</TouchableOpacity>
						</View>
					) : isMobileNative ? (
						<>
							<View style={styles.mobileSortSection}>
								<View style={[styles.mobileSortToggleContainer, isDark && { backgroundColor: colors.background.elevated }]}>
									<TouchableOpacity
										style={[styles.mobileSortToggleButton, selectedSortMode === 'BIGGEST_DELTA' && styles.mobileSortToggleButtonActive, selectedSortMode === 'BIGGEST_DELTA' && isDark && { backgroundColor: colors.background.surface }]}
										onPress={() => handleSortModeChange('BIGGEST_DELTA')}
										accessibilityRole="button"
										accessibilityLabel="Sort by Biggest Delta"
									>
										<Text style={[styles.mobileSortToggleButtonText, isDark && { color: colors.text.secondary }, selectedSortMode === 'BIGGEST_DELTA' && styles.mobileSortToggleButtonTextActive, selectedSortMode === 'BIGGEST_DELTA' && isDark && { color: colors.brand.primary }]}>Biggest Delta</Text>
									</TouchableOpacity>
									<TouchableOpacity
										style={[styles.mobileSortToggleButton, selectedSortMode === 'SPARC_CATEGORY' && styles.mobileSortToggleButtonActive, selectedSortMode === 'SPARC_CATEGORY' && isDark && { backgroundColor: colors.background.surface }]}
										onPress={() => handleSortModeChange('SPARC_CATEGORY')}
										accessibilityRole="button"
										accessibilityLabel="Sort by SPARC Category"
									>
										<Text style={[styles.mobileSortToggleButtonText, isDark && { color: colors.text.secondary }, selectedSortMode === 'SPARC_CATEGORY' && styles.mobileSortToggleButtonTextActive, selectedSortMode === 'SPARC_CATEGORY' && isDark && { color: colors.brand.primary }]}>SPARC Category</Text>
									</TouchableOpacity>
								</View>
							</View>

							{selectedSortMode === 'BIGGEST_DELTA' ? renderBiggestDelta() : renderSPARCCategories()}
						</>
					) : (
						<>
							{/* Search bar */}
							<View style={styles.kpiSearchRow}>
								<View style={[styles.kpiSearchInputWrap, isSearchFocused && styles.kpiSearchInputWrapFocused, isDark && { backgroundColor: colors.input.background, borderColor: colors.input.border }]}>
									<MaterialIcons name="search" size={18} color={isDark ? colors.text.disabled : '#7C8086'} />
									<TextInput
										value={searchTerm}
										onChangeText={setSearchTerm}
										onFocus={() => setIsSearchFocused(true)}
										onBlur={() => setIsSearchFocused(false)}
										placeholder="Search metrics by name or category…"
										placeholderTextColor={isDark ? colors.text.disabled : '#9AA1AB'}
										style={[styles.kpiSearchInput, Platform.OS === 'web' ? ({ outlineStyle: 'none', boxShadow: 'none' } as any) : null, isDark && { color: colors.text.primary }]}
									/>
									{searchTerm ? (
										<TouchableOpacity onPress={() => setSearchTerm('')} accessibilityRole="button" accessibilityLabel="Clear metric search" style={[styles.kpiSearchClearButton, isDark && { backgroundColor: colors.background.elevated }]}>
											<MaterialIcons name="close" size={16} color={isDark ? colors.text.secondary : '#6D737C'} />
										</TouchableOpacity>
									) : null}
								</View>
								<View style={styles.kpiSearchMetaWrap}>
									<Text style={[styles.kpiSearchResultCount, isDark && { color: colors.text.secondary }]}>{filteredMetrics.length} METRICS</Text>
									<Text style={[styles.sortCardsLabel, isDark && { color: colors.text.secondary }]}>SORT CARDS</Text>
									<View style={[styles.sortToggleContainer, isDark && { backgroundColor: colors.background.elevated }]}>
										<TouchableOpacity
											style={[styles.sortToggleButton, selectedSortMode === 'BIGGEST_DELTA' && styles.sortToggleButtonActive, selectedSortMode === 'BIGGEST_DELTA' && isDark && { backgroundColor: colors.background.surface }]}
											onPress={() => handleSortModeChange('BIGGEST_DELTA')}
											accessibilityRole="button"
											accessibilityLabel="Sort by Biggest Delta"
										>
											<Text style={[styles.sortToggleButtonText, isDark && { color: colors.text.secondary }, selectedSortMode === 'BIGGEST_DELTA' && styles.sortToggleButtonTextActive, selectedSortMode === 'BIGGEST_DELTA' && isDark && { color: colors.brand.primary }]}>Biggest Delta</Text>
										</TouchableOpacity>
										<TouchableOpacity
											style={[styles.sortToggleButton, selectedSortMode === 'SPARC_CATEGORY' && styles.sortToggleButtonActive, selectedSortMode === 'SPARC_CATEGORY' && isDark && { backgroundColor: colors.background.surface }]}
											onPress={() => handleSortModeChange('SPARC_CATEGORY')}
											accessibilityRole="button"
											accessibilityLabel="Sort by SPARC Category"
										>
											<Text style={[styles.sortToggleButtonText, isDark && { color: colors.text.secondary }, selectedSortMode === 'SPARC_CATEGORY' && styles.sortToggleButtonTextActive, selectedSortMode === 'SPARC_CATEGORY' && isDark && { color: colors.brand.primary }]}>SPARC Category</Text>
										</TouchableOpacity>
									</View>
								</View>
							</View>
	
							{/* Category filter chips */}
							<View style={styles.chipsRow}>
							<TouchableOpacity style={[styles.filterChip, isMobileNative && styles.filterChipMobile, isAllChipActive && styles.filterChipActive, isDark && !isAllChipActive && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }]} onPress={() => setSelectedCategories([])} accessibilityRole="button" accessibilityLabel="Show all categories">
								<Text style={[styles.filterChipText, isDark && !isAllChipActive && { color: colors.text.secondary }, isAllChipActive && styles.filterChipTextActive]}>All</Text>
								<View style={[styles.filterChipCountBadge, isAllChipActive && styles.filterChipCountBadgeActive, isDark && !isAllChipActive && { backgroundColor: colors.background.surface }]}>
									<Text style={[styles.filterChipCountText, isDark && !isAllChipActive && { color: colors.text.secondary }, isAllChipActive && styles.filterChipCountTextActive]}>{metrics.length}</Text>
								</View>
							</TouchableOpacity>
							{KPI_CATEGORY_FILTER_ORDER.map(category => {
								const count = categoryCounts[category];
								const isActive = selectedCategories.includes(category);
								const categoryPalette = isDark ? KPI_CATEGORY_COLORS_DARK[category] : KPI_CATEGORY_COLORS[category];
								return (
									<TouchableOpacity
										key={category}
										style={[
											styles.filterChip,
											isDark && !isActive && { backgroundColor: colors.background.elevated, borderColor: colors.border.default },
											isActive && { backgroundColor: categoryPalette.background, borderColor: categoryPalette.background },
										]}
										onPress={() => toggleCategory(category)}
										accessibilityRole="button"
										accessibilityLabel={`Filter by ${category}`}
									>
										<View style={[styles.filterChipDot, { backgroundColor: categoryPalette.icon }]} />
										<Text style={[styles.filterChipText, isDark && !isActive && { color: colors.text.secondary }, isActive && { color: categoryPalette.text }]}>{category}</Text>
										<View style={[styles.filterChipCountBadge, isDark && !isActive && { backgroundColor: colors.background.surface }, isActive && { backgroundColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)' }]}>
											<Text style={[styles.filterChipCountText, isDark && !isActive && { color: colors.text.secondary }, isActive && { color: categoryPalette.text }]}>{count}</Text>
										</View>
									</TouchableOpacity>
								);
							})}
							</View>
	
							{filteredMetrics.length === 0 && searchTerm.trim() ? (
								<View style={[styles.kpiSearchEmptyState, isDark && { backgroundColor: colors.background.surface, borderColor: colors.border.default }]}>
									<MaterialIcons name="manage-search" size={26} color={isDark ? colors.brand.primary : '#0B5FC6'} />
									<Text style={[styles.kpiSearchEmptyTitle, isDark && { color: colors.text.primary }]}>No metrics match your search</Text>
									<Text style={[styles.kpiSearchEmptyText, isDark && { color: colors.text.secondary }]}>Try a different keyword or clear your search.</Text>
									<TouchableOpacity style={styles.kpiSearchClearFiltersBtn} onPress={() => setSearchTerm('')} accessibilityRole="button" accessibilityLabel="Clear metric search">
										<Text style={styles.kpiSearchClearFiltersBtnText}>Clear search</Text>
									</TouchableOpacity>
								</View>
							) : selectedSortMode === 'BIGGEST_DELTA' ? (
								renderBiggestDelta()
							) : (
								renderSPARCCategories()
							)}
						</>
					)}
				</View>
			) : null}

			<KPITrendModal
				visible={modalVisible}
				metric={selectedKpi}
				locationToken={locationListToken}
				yearToken={selectedYearToken}
				periodToken={selectedPeriodToken}
				weekToken={selectedWeekToken}
				onClose={closeModal}
			/>
		</View>
	);
};

export default KPIOverviewScreen;
